import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { contractsApi } from '../../services/api';
import { format } from 'date-fns';
import MobileCardList from '../../components/MobileCardList';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import ResponsiveHeader from '../../components/ResponsiveHeader';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const CONTRACT_TYPE_LABELS = {
  domestic: 'Domestico',
  commercial: 'Commerciale',
  industrial: 'Industriale',
  agricultural: 'Agricolo',
  resale: 'Rivendita',
};

const ContractList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', { page, pageSize, search: searchQuery, type: filterType, active: filterActive }],
    queryFn: () => contractsApi.getAll({
      page: page + 1,
      limit: pageSize,
      search: searchQuery,
      contract_type: filterType !== 'all' ? filterType : undefined,
      is_active: filterActive !== 'all' ? filterActive === 'active' : undefined,
    }).then(res => ({
      contracts: res.data?.data || res.data?.contracts || [],
      total: res.data?.pagination?.total || res.data?.total || 0
    })),
    placeholderData: (prev) => prev
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => contractsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setDeleteDialogOpen(false);
    },
  });

  const handleMenuOpen = (event, contract) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedContract(contract);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const columns = [
    { field: 'contract_number', headerName: 'N° Contratto', width: 120 },
    {
      field: 'customer',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => 
        params.row.Customer?.business_name || 
        `${params.row.Customer?.last_name} ${params.row.Customer?.first_name}`,
    },
    {
      field: 'contract_type',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip label={CONTRACT_TYPE_LABELS[params.value] || params.value} size="small" variant="outlined" />
      ),
    },
    { field: 'installation_code', headerName: 'Cod. Inst.', width: 100 },
    {
      field: 'installation_city',
      headerName: 'Località',
      width: 150,
    },
    {
      field: 'start_date',
      headerName: 'Data Inizio',
      width: 120,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '',
    },
    {
      field: 'price_per_unit',
      headerName: 'Prezzo/Lt',
      width: 110,
      align: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'is_active',
      headerName: 'Stato',
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value ? 'Attivo' : 'Chiuso'} size="small" color={params.value ? 'success' : 'default'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Prepare rows with computed customer name for mobile
  const contractRows = (data?.contracts || []).map(c => ({
    ...c,
    customerName: c.Customer?.business_name || `${c.Customer?.last_name || ''} ${c.Customer?.first_name || ''}`.trim(),
    typeLabel: CONTRACT_TYPE_LABELS[c.contract_type] || c.contract_type,
    startDateFormatted: c.start_date ? format(new Date(c.start_date), 'dd/MM/yyyy') : '',
  }));

  return (
    <Box>
      <ResponsiveHeader
        title="Contratti"
        subtitle="Gestione contratti di fornitura GPL"
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Contratto',
          icon: <AddIcon />,
          onClick: () => navigate('/crm/contratti/nuovo'),
        }}
      />

      <Card sx={{ mb: isMobile ? 1.5 : 3 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
          <ResponsiveFilters
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Cerca per cliente, numero contratto..."
            activeFilterCount={(filterType !== 'all' ? 1 : 0) + (filterActive !== 'all' ? 1 : 0)}
            onClearFilters={() => { setFilterType('all'); setFilterActive('all'); }}
          >
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Tipo">
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="domestic">Domestico</MenuItem>
                <MenuItem value="commercial">Commerciale</MenuItem>
                <MenuItem value="industrial">Industriale</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Stato</InputLabel>
              <Select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} label="Stato">
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="active">Attivi</MenuItem>
                <MenuItem value="inactive">Chiusi</MenuItem>
              </Select>
            </FormControl>
          </ResponsiveFilters>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {error ? (
            <Alert severity="error" sx={{ m: 2 }}>Errore nel caricamento</Alert>
          ) : isMobile ? (
            <MobileCardList
              rows={contractRows}
              loading={isLoading}
              onRowClick={(row) => navigate(`/crm/contratti/${row.id}`)}
              primaryField="customerName"
              secondaryField="contract_number"
              statusField={{
                field: 'is_active',
                labelMap: { true: 'Attivo', false: 'Chiuso', 1: 'Attivo', 0: 'Chiuso' },
                colorMap: { true: 'success', false: 'default', 1: 'success', 0: 'default' },
              }}
              extraFields={[
                { field: 'typeLabel', label: 'Tipo' },
                { field: 'startDateFormatted', label: 'Inizio' },
                { field: 'installation_city', label: 'Località' },
              ]}
              pagination={{
                page,
                pageSize,
                total: data?.total || 0,
                onPageChange: (newPage) => setPage(newPage),
              }}
              emptyMessage="Nessun contratto trovato"
            />
          ) : (
            <DataGrid
              rows={data?.contracts || []}
              columns={columns}
              loading={isLoading}
              paginationMode="server"
              rowCount={data?.total || 0}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
              pageSizeOptions={[10, 25, 50]}
              autoHeight
              disableSelectionOnClick
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 'none' }}
              onRowClick={(params) => navigate(`/crm/contratti/${params.id}`)}
            />
          )}
        </CardContent>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { handleMenuClose(); navigate(`/crm/contratti/${selectedContract?.id}`); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>Visualizza
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); navigate(`/crm/contratti/${selectedContract?.id}/modifica`); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Modifica
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); navigate(`/crm/clienti/${selectedContract?.customer_id}`); }}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>Vai al Cliente
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Elimina
        </MenuItem>
      </Menu>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>Eliminare il contratto <strong>{selectedContract?.contract_number}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button onClick={() => deleteMutation.mutate(selectedContract?.id)} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractList;
